import parliamentarianUsers from 'components/Dashboard/SidebarElements/parliamentarianUsers';
import TransformFromPascalCase from 'components/Settings/Utilities/TransformFromPascalCase';
import transformToPascalCase from 'components/Settings/Utilities/TransformToPascalCase';
import { store } from 'components/Store';
import ConfigData from 'config/ConfigData';
import { useContext } from 'react';
import { useSiteSectionsNames } from 'utils/getAppSite';

const changeItemsName = (item) => {
  switch (item) {
    case 'Upcoming events':
      return 'Events';
    case 'Library material':
      return 'CommonsLibraryArticles';
    case 'Parliament live':
      return 'RealtimeUtterances';
    default:
      return transformToPascalCase(item);
  }
};

const useCreateResourcesName = () => {
  const { getName } = useSiteSectionsNames();
  const globalState = useContext(store);
  const { state } = globalState;
  const { activeSubscriptions } = state;
  const { planCode } = activeSubscriptions;
  const showTweets = import.meta.env.VITE_APP_ENABLE_TWITTER === 'true';

  //AE: Transform resources for the website feeds component
  const createResourcesName = (contentTypesToReceive, referenceValues, code) => {
    const transformReferenceValues = (referenceValues) => {
      return referenceValues.map((item) => changeItemsName(item));
    };

    let resources = referenceValues
      ? transformReferenceValues(referenceValues)
      : ConfigData.createResourcesNameResources;

    let resourcesToReceive = contentTypesToReceive.split(', ');

    if (import.meta.env.VITE_APP_ENABLE_TWITTER === 'false') {
      resources = resources.filter((item) => item !== 'Tweets' && item !== 'Retweets');
      resourcesToReceive = resourcesToReceive.filter((item) => item !== 'Tweets' && item !== 'Retweets');
    }

    let exceptResources = resources.filter((type) => !resourcesToReceive.includes(type));
    const correctResourceName = (name) => {
      switch (name) {
        case 'AnsweredWrittenQuestion':
          return 'Answered written questions';
        case 'UnansweredWrittenQuestion':
          return 'Newly tabled written questions';
        case 'HandPickedKeyUpdates':
          return 'Key updates';
        case 'HansardContent':
          return 'Official records';
        case 'UserContent':
          return code && parliamentarianUsers(code) ? 'Campaigns' : 'Lobbying material';
        case 'RealtimeUtterances':
          return 'Parliament live';
        case 'CommonsLibraryArticles':
          return 'Library material';
        case 'Edms':
          return 'Early Day Motions';
        case 'Tweets':
          return 'X posts';
        case 'Retweets':
          return 'X reposts';
        default:
          return TransformFromPascalCase(name);
      }
    };
    const orderResources = (resources) => {
      let resourcesDropdownOrder =
        referenceValues ??
        (parliamentarianUsers(planCode)
          ? ConfigData.initialContentResources?.filter((item) => item !== 'Lobbying material')
          : ConfigData.initialContentResources);
      const localResources = [...resourcesDropdownOrder];
      if (showTweets && !resourcesDropdownOrder.includes('Tweets')) {
        localResources.push('X posts');
      }
      return localResources.filter((item) => resources.includes(item));
    };

    const createResourcesValue = () => {
      if (exceptResources.length === 1) {
        return `Everything except ${getName({ property: correctResourceName(exceptResources[0]) })}`;
      } else if (exceptResources.length === 2) {
        if (
          exceptResources.includes('AnsweredWrittenQuestion') &&
          exceptResources.includes('UnansweredWrittenQuestion')
        ) {
          return `Everything except Written questions and answers`;
        }
        if (exceptResources.includes('Tweets') && exceptResources.includes('Retweets')) {
          return `Everything except X posts`;
        }
        return `Everything except ${getName({ property: correctResourceName(exceptResources[0]) })} and ${getName({
          property: correctResourceName(exceptResources[1]),
        })}`;
      } else if (
        exceptResources.includes('AnsweredWrittenQuestion') &&
        exceptResources.includes('UnansweredWrittenQuestion') &&
        (exceptResources.length === 3 ||
          (exceptResources.length === 4 && exceptResources.includes('Tweets') && exceptResources.includes('Retweets')))
      ) {
        if (exceptResources.length === 3) {
          const remainingResource = exceptResources.find(
            (item) => item !== 'AnsweredWrittenQuestion' && item !== 'UnansweredWrittenQuestion'
          );
          return `Everything except Q&As and ${getName({ property: correctResourceName(remainingResource) })}`;
        } else {
          return `Everything except Q&As and X posts`;
        }
      } else {
        const includesBothQandA =
          resourcesToReceive.includes('AnsweredWrittenQuestion') && resourcesToReceive.includes('UnansweredWrittenQuestion');
        const otherResources = includesBothQandA
          ? resourcesToReceive.filter(
              (item) => item !== 'AnsweredWrittenQuestion' && item !== 'UnansweredWrittenQuestion'
            )
          : resourcesToReceive;
        const orderedOtherNames = orderResources(otherResources.map(correctResourceName)).map((item) =>
          getName({ property: correctResourceName(item) })
        );
        if (!includesBothQandA) {
          return orderedOtherNames.join(', ');
        }
        const qAndALabel = orderedOtherNames.length === 0 ? 'All written questions' : 'All Q&As';
        return [...orderedOtherNames, qAndALabel].join(', ');
      }
    };
    const value = resourcesToReceive.length === resources.length ? 'Everything' : createResourcesValue();
    return value;
  };
  return { createResourcesName };
};

export { changeItemsName };
export default useCreateResourcesName;
